import { v4 as uuidv4 } from 'uuid';
import { ErrorName, PlayerError } from '../../errors';
import BaseEngine from '../BaseEngine';
const MP3_MIME_TYPE = 'audio/mpeg';
const METADATA_LENGTH_SPECIFIER_MULTIPLIER = 16;
const METADATA_MAX_LENGTH = 4080;
const formatToMimeTypeMap = {
    mp3: 'audio/mpeg',
    aac: 'audio/aac',
};
export default class IcyEngine extends BaseEngine {
    static name = 'icy';
    static get MediaSourceApi() {
        return (('MediaSource' in window && MediaSource) ||
            ('ManagedMediaSource' in window &&
                ManagedMediaSource));
    }
    static browserSupportsFormat(format) {
        return IcyEngine.MediaSourceApi?.isTypeSupported(formatToMimeTypeMap[format]);
    }
    #fetchAbortController = null;
    #textDecoder;
    #sourceBuffer;
    #isStopped = false;
    #loadId = null;
    constructor(options) {
        super(options);
        this.#textDecoder = new TextDecoder();
    }
    async #decodeAndParseMetadata(metadataChunk) {
        try {
            const rawMetadata = this.#textDecoder?.decode(Uint8Array.from(metadataChunk));
            if (!rawMetadata) {
                throw new Error('Received empty metadata');
            }
            this.emit('metadata', { icy: rawMetadata });
        }
        catch (error) {
            const message = `Error parsing ICY metadata: ${error?.message}`;
            this.emit('error', PlayerError.createError(ErrorName.MetadataError, message));
        }
    }
    #fetchStream = async () => {
        this.#fetchAbortController = new AbortController();
        try {
            const response = await fetch(this._options.src, {
                headers: { 'Icy-Metadata': '1' },
                signal: this.#fetchAbortController?.signal,
            });
            if (!response.ok || !response.body) {
                throw new Error('Failed to load the stream');
            }
            const icyMetadataInterval = Number(response.headers.get('icy-metaint'));
            if (!icyMetadataInterval) {
                throw new Error('ICY metadata interval not provided by the server');
            }
            return { reader: response.body.getReader(), icyMetadataInterval };
        }
        catch (error) {
            if (error.name === 'AbortError') {
                throw error;
            }
            const message = `Stream fetch with ICY headers failed: ${error?.message}`;
            this.emit('error', PlayerError.createError(ErrorName.MetadataError, message));
        }
        try {
            const response = await fetch(this._options.src, {
                signal: this.#fetchAbortController?.signal,
            });
            if (!response.ok || !response.body) {
                throw new Error(`Failed to load the stream. Status: ${response.status}`);
            }
            return { reader: response.body.getReader() };
        }
        catch (error) {
            if (error.name === 'AbortError') {
                throw error;
            }
            const message = `Stream fetch without ICY headers failed: ${error?.message}`;
            this.emit('error', PlayerError.createError(ErrorName.MetadataError, message));
            throw error;
        }
    };
    #waitForSourceBufferUpdateEnd() {
        const { promise, resolve } = Promise.withResolvers();
        this.#sourceBuffer?.addEventListener('updateend', resolve, { once: true });
        return promise;
    }
    async #appendToMediaSourceBuffer(audioChunk) {
        const sourceBufferUpdatePromise = this.#waitForSourceBufferUpdateEnd();
        this.#sourceBuffer?.appendBuffer(audioChunk);
        await sourceBufferUpdatePromise;
    }
    async #endMediaSourceStream(reason) {
        if (this.#sourceBuffer?.updating) {
            await this.#waitForSourceBufferUpdateEnd();
        }
        if (this._mediaSource?.readyState === 'open') {
            this._mediaSource.endOfStream(reason);
        }
        this.#sourceBuffer = undefined;
    }
    #fetchAndProcessStream = async () => {
        const byteAccumulator = [];
        try {
            const { reader, icyMetadataInterval } = await this.#fetchStream();
            this.#sourceBuffer?.addEventListener('error', () => {
                this.#endMediaSourceStream('decode');
            });
            while (true) {
                const { done, value: rawChunk } = await reader.read();
                if (this._mediaSource.readyState === 'ended' || this.#isStopped) {
                    break;
                }
                if (done) {
                    this.#endMediaSourceStream();
                    break;
                }
                if (!icyMetadataInterval) {
                    await this.#appendToMediaSourceBuffer(rawChunk);
                    continue;
                }
                for (const byte of rawChunk) {
                    byteAccumulator.push(byte);
                    if (byteAccumulator.length <=
                        icyMetadataInterval + METADATA_MAX_LENGTH) {
                        continue;
                    }
                    const pureAudioBytes = byteAccumulator.splice(0, icyMetadataInterval);
                    const metadataLengthSpecifier = byteAccumulator.shift();
                    if (metadataLengthSpecifier) {
                        const metadataLength = metadataLengthSpecifier * METADATA_LENGTH_SPECIFIER_MULTIPLIER;
                        const metadataBytes = byteAccumulator.splice(0, metadataLength);
                        this.#decodeAndParseMetadata(metadataBytes);
                    }
                    await this.#appendToMediaSourceBuffer(Uint8Array.from(pureAudioBytes));
                }
            }
        }
        catch (error) {
            if (error.name === 'AbortError') {
                return;
            }
            throw error;
        }
    };
    #onMediaSourceOpen = async () => {
        const currentLoadId = this.#loadId;
        this.#sourceBuffer = this._mediaSource.addSourceBuffer(MP3_MIME_TYPE);
        try {
            await this.#fetchAndProcessStream();
        }
        catch (error) {
            if (currentLoadId !== this.#loadId || this.#isStopped) {
                return;
            }
            if (this.isPlaying()) {
                this.emit('error', PlayerError.createError(ErrorName.PlaybackErrorNetwork, error?.message));
            }
            this.destroy();
        }
    };
    #abortFetch() {
        try {
            this.#fetchAbortController?.abort();
        }
        catch {
        }
    }
    async load() {
        this.#loadId = uuidv4();
        this.#isStopped = false;
        this.#fetchAbortController = null;
        this._mediaSource = new IcyEngine.MediaSourceApi();
        this._mediaSource.addEventListener('sourceopen', this.#onMediaSourceOpen);
        return super.load();
    }
    destroy() {
        this.#loadId = null;
        this.#isStopped = true;
        this.#abortFetch();
        this.#endMediaSourceStream();
        super.destroy();
    }
}
